import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {ActionButton} from '@edna/components';
import AddIcon from '@edna/icons/add.svg';

import {
  CHANNEL_LABEL,
  EChannelType,
  TChannelType,
  TItemId,
  TSubject,
  channelIconHash,
} from 'src/constants';
import {useGetAllSubjectsQuery} from 'src/containers/Subjects/api';
import {TARIFF_CHANNEL_TYPES} from 'src/containers/TariffsCatalog';
import {EPermission, usePermission} from 'src/containers/User';

import {TariffCard} from '../TariffCard';
import {showCreateTariffModal} from '../TariffModals';
import {useGetAvailableTariffsQuery, useGetConnectedTariffsQuery} from '../api';
import {
  ETariffViewType,
  TAvailableTariffResponse,
  TCreateAvailableTariffFormData,
} from '../definitions';
import {getLinkedSubjects} from '../utils';
import * as S from './style';

export const AddAvailableTariffButton = React.memo<{
  channelTypes?: EChannelType[];
  onSubmit?: (tariff: TCreateAvailableTariffFormData) => void;
}>(({onSubmit, channelTypes = TARIFF_CHANNEL_TYPES}) => {
  const {t} = useTranslation();

  const addTariffActions = React.useMemo(() => {
    return channelTypes.map((channelType: EChannelType) => ({
      content: (
        <>
          <S.LeftIcon as={channelIconHash[channelType]} indent="2" />
          {t('Tariffs:allTariffs.tariffOption', {channelType: CHANNEL_LABEL[channelType]})}
        </>
      ),
      handler: () => showCreateTariffModal({channelType, onSubmit}),
    }));
  }, [t, onSubmit]);

  return (
    <ActionButton options={addTariffActions} size="S" overlayPlacementX="left">
      <S.LeftIcon as={AddIcon} />
      {t('Tariffs:allTariffs.addTariff')}
    </ActionButton>
  );
});

AddAvailableTariffButton.displayName = 'AddAvailableTariffButton';

export const SupportTariffsList = React.memo(() => {
  const {t} = useTranslation();
  const {id: tenantId} = useParams();
  const tariffsRef = React.useRef<Record<number, HTMLDivElement | null>>({});
  const hasTariffExtraPermission = usePermission(EPermission.PERMISSION_TENANT_TARIFF_WRITE);

  const {data: tariffs = []} = useGetAvailableTariffsQuery({tenantId}, {skip: !tenantId});
  const {data: connectedTariffs = [], fulfilledTimeStamp: connectedTariffsTimeStamp} =
    useGetConnectedTariffsQuery(tenantId ?? skipToken, {
      skip: !tariffs.length,
    });
  const {data: allSubjects = []} = useGetAllSubjectsQuery(
    {tenantId},
    {
      skip: !connectedTariffs.length || !tariffs.length,
    },
  );

  const tariffsById = React.useMemo<Record<number, TAvailableTariffResponse>>(() => {
    if (!tariffs.length) {
      return {};
    }

    return tariffs.reduce((result, item) => ({...result, [item.active.id]: item}), {});
  }, [tariffs]);

  const subjectsById = React.useMemo<Record<number, TSubject>>(() => {
    if (!allSubjects.length) {
      return {};
    }

    return allSubjects.reduce((result, item) => ({...result, [item.id]: item}), {});
  }, [allSubjects]);

  const linkedSubjects = React.useMemo(
    () => getLinkedSubjects(connectedTariffs),
    [connectedTariffsTimeStamp],
  );

  const scrollToTariff = React.useCallback((itemId: number) => {
    if (tariffsRef.current?.[itemId]) {
      tariffsRef.current[itemId]?.scrollIntoView({behavior: 'smooth'});
    }
  }, []);

  const tariffViewType = hasTariffExtraPermission
    ? ETariffViewType.BILLING_DEPARTMENT_ACCOUNT
    : ETariffViewType.SUPPORT;

  if (!tariffs.length) {
    return <AddAvailableTariffButton />;
  }

  return (
    <>
      {!!connectedTariffs.length && (
        <S.CollapsibleSection label={t('Tariffs:allTariffs.connectedTariffs')} isOpenDefault>
          <S.Tariffs>
            {connectedTariffs.map((connectedTariff) => {
              const subjectId = connectedTariff.active.subjectId;
              const availableTariff = tariffsById[connectedTariff.active.selectedTariffId];

              if (!availableTariff) {
                return null;
              }

              return (
                <TariffCard
                  onTariffLinkClick={() => scrollToTariff(availableTariff.active.id)}
                  subject={subjectsById[subjectId]}
                  availableTariff={availableTariff}
                  connectedTariff={connectedTariff}
                  key={`${connectedTariff.active.id}_${connectedTariff.active.version}_${subjectId}`}
                  viewType={tariffViewType}
                />
              );
            })}
          </S.Tariffs>
        </S.CollapsibleSection>
      )}
      <S.CollapsibleSection
        label={t('Tariffs:allTariffs.availableTariffs')}
        isOpenDefault={!connectedTariffs.length}
      >
        <S.Tariffs>
          <AddAvailableTariffButton />
          {tariffs.map((tariff) => (
            <TariffCard
              innerRef={(element) => {
                tariffsRef.current[tariff.active.id] = element;
              }}
              linkedSubjects={linkedSubjects[tariff.active.id]}
              availableTariff={tariff}
              key={`${tariff.active.id}_${tariff.active.version}`}
              viewType={tariffViewType}
            />
          ))}
        </S.Tariffs>
      </S.CollapsibleSection>
    </>
  );
});

SupportTariffsList.displayName = 'SupportTariffsList';

export const AvailableTariffsList = React.memo<{
  tenantId?: TItemId;
  channelType?: TChannelType;
  noTariffsNode?: React.ReactNode;
  showConnectedTariffs?: boolean;
}>(({tenantId, channelType, noTariffsNode, showConnectedTariffs}) => {
  const {tariffs, isLoading: isLoadingTariffs} = useGetAvailableTariffsQuery(
    {tenantId, channelType},
    {
      selectFromResult: ({data, isLoading}) => ({
        tariffs: data?.filter((tariff) => tariff.active.visible) ?? [],
        isLoading,
      }),
    },
  );

  const {
    data: connectedTariffs = [],
    fulfilledTimeStamp: connectedTariffsTimeStamp,
    isLoading: isLoadingConnected,
  } = useGetConnectedTariffsQuery(tenantId, {
    skip: !tariffs.length || !showConnectedTariffs,
  });

  const linkedSubjects = React.useMemo(
    () => getLinkedSubjects(connectedTariffs),
    [connectedTariffsTimeStamp],
  );

  if (isLoadingTariffs || isLoadingConnected) {
    return null;
  }

  if (!tariffs.length && noTariffsNode) {
    return <>{noTariffsNode}</>;
  }

  return (
    <>
      {tariffs.map((tariff) => (
        <TariffCard
          linkedSubjects={linkedSubjects[tariff.active.id]}
          availableTariff={tariff}
          key={`${tariff.active.id}_${tariff.active.version}`}
          viewType="CLIENT"
        />
      ))}
    </>
  );
});

AvailableTariffsList.displayName = 'AvailableTariffsList';

export const ManagerTariffsList = React.memo<{tenantId?: TItemId}>(({tenantId}) => {
  const {data: tariffs = []} = useGetAvailableTariffsQuery({tenantId}, {skip: !tenantId});

  return (
    <>
      {tariffs.map((tariff) => (
        <TariffCard availableTariff={tariff} key={tariff.active.id} viewType="MANAGER" />
      ))}
    </>
  );
});

ManagerTariffsList.displayName = 'ManagerTariffsList';
